import { useCallback, useRef, useState } from "react";
import { family, emailVerification, type ChildProfileItem } from "../api";
import { newAudioRecorder } from "./recorderMimeType";
import { bytesToBase64 } from "../platform/types";

// ============================================================================
// 14.1 — Invite Co-Parent Flow
// ============================================================================

interface InviteCoParentProps {
  onClose: () => void;
  onSuccess: () => void;
}

export function InviteCoParentFlow({ onClose, onSuccess }: InviteCoParentProps) {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    setError("");
    try {
      await family.sendInvitation(email.trim());
      setSent(true);
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to send invitation");
    } finally {
      setSubmitting(false);
    }
  }

  if (sent) {
    return (
      <div style={modalStyles.overlay} role="dialog" aria-modal="true">
        <div style={modalStyles.content}>
          <h2 style={modalStyles.title}>Invitation Sent! ✉️</h2>
          <p style={modalStyles.text}>
            We sent an invitation to <strong>{email}</strong>. They have 72 hours to accept.
          </p>
          <button onClick={onClose} style={modalStyles.primaryButton}>
            Done
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={modalStyles.overlay} role="dialog" aria-modal="true">
      <div style={modalStyles.content}>
        <h2 style={modalStyles.title}>Invite Co-Parent</h2>
        <p style={modalStyles.text}>
          Enter their email address. They&apos;ll get a link to join your family account.
        </p>
        <form onSubmit={handleSubmit}>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="partner@email.com"
            style={modalStyles.input}
            autoFocus
            aria-label="Co-parent email"
          />
          {error && <p style={modalStyles.error}>{error}</p>}
          <div style={modalStyles.buttonRow}>
            <button
              type="submit"
              disabled={!email.trim() || submitting}
              style={modalStyles.primaryButton}
            >
              {submitting ? "Sending..." : "Send Invitation"}
            </button>
            <button type="button" onClick={onClose} style={modalStyles.secondaryButton}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================================================
// 14.2 — Delete Child Profile Confirmation
// ============================================================================

interface DeleteChildDialogProps {
  child: ChildProfileItem;
  onClose: () => void;
  onDeleted: () => void;
}

export function DeleteChildDialog({ child, onClose, onDeleted }: DeleteChildDialogProps) {
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  async function handleDelete(e: React.FormEvent) {
    e.preventDefault();
    if (!password.trim()) return;
    setSubmitting(true);
    setError("");
    try {
      await family.deleteChild(child.id, password);
      onDeleted();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete profile");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={modalStyles.overlay} role="dialog" aria-modal="true">
      <div style={modalStyles.content}>
        <h2 style={modalStyles.title}>Delete {child.username}&apos;s Profile?</h2>
        <p style={modalStyles.text}>
          This will hide their profile and projects. You can restore it within 30 days.
        </p>
        <form onSubmit={handleDelete}>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter your password to confirm"
            style={modalStyles.input}
            autoFocus
            aria-label="Password confirmation"
          />
          {error && <p style={modalStyles.error}>{error}</p>}
          <div style={modalStyles.buttonRow}>
            <button
              type="submit"
              disabled={!password.trim() || submitting}
              style={modalStyles.dangerButton}
            >
              {submitting ? "Deleting..." : "Delete Profile"}
            </button>
            <button type="button" onClick={onClose} style={modalStyles.secondaryButton}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================================================
// 14.3 — Restore Child Profile
// ============================================================================

interface RestoreChildDialogProps {
  childId: string;
  childName: string;
  onClose: () => void;
  onRestored: () => void;
}

export function RestoreChildDialog({
  childId,
  childName,
  onClose,
  onRestored,
}: RestoreChildDialogProps) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  async function handleRestore() {
    setSubmitting(true);
    setError("");
    try {
      await family.restoreChild(childId);
      onRestored();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to restore profile");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={modalStyles.overlay} role="dialog" aria-modal="true">
      <div style={modalStyles.content}>
        <h2 style={modalStyles.title}>Restore {childName}&apos;s Profile?</h2>
        <p style={modalStyles.text}>
          This will restore their profile and all associated projects.
        </p>
        {error && <p style={modalStyles.error}>{error}</p>}
        <div style={modalStyles.buttonRow}>
          <button
            onClick={handleRestore}
            disabled={submitting}
            style={modalStyles.primaryButton}
          >
            {submitting ? "Restoring..." : "Restore Profile"}
          </button>
          <button onClick={onClose} style={modalStyles.secondaryButton}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// 14.4 — Passphrase Management UI
// ============================================================================

interface PassphraseManagementProps {
  child: ChildProfileItem;
  onClose: () => void;
  onUpdated: () => void;
}

export function PassphraseManagement({
  child,
  onClose,
  onUpdated,
}: PassphraseManagementProps) {
  const [passphrase, setPassphrase] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [showRecordVariant, setShowRecordVariant] = useState(false);

  async function handleSetPassphrase(e: React.FormEvent) {
    e.preventDefault();
    if (!passphrase.trim()) return;
    setSubmitting(true);
    setError("");
    try {
      await family.setPassphrase(child.id, passphrase.trim());
      setSuccess(true);
      onUpdated();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to set passphrase");
    } finally {
      setSubmitting(false);
    }
  }

  if (showRecordVariant) {
    return (
      <SpokenVariantRecording
        childId={child.id}
        childName={child.username}
        onClose={() => setShowRecordVariant(false)}
        onRecorded={() => {
          setShowRecordVariant(false);
          onUpdated();
        }}
      />
    );
  }

  if (success) {
    return (
      <div style={modalStyles.overlay} role="dialog" aria-modal="true">
        <div style={modalStyles.content}>
          <h2 style={modalStyles.title}>Passphrase Set! ✅</h2>
          <p style={modalStyles.text}>
            {child.username} can now log in with their secret phrase.
          </p>
          <p style={modalStyles.text}>
            Want to record them saying it? This helps if their speech is transcribed differently.
          </p>
          <div style={modalStyles.buttonRow}>
            <button onClick={() => setShowRecordVariant(true)} style={modalStyles.primaryButton}>
              Record Spoken Variant
            </button>
            <button onClick={onClose} style={modalStyles.secondaryButton}>
              Skip for Now
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={modalStyles.overlay} role="dialog" aria-modal="true">
      <div style={modalStyles.content}>
        <h2 style={modalStyles.title}>
          {child.has_passphrase ? "Reset" : "Set"} Passphrase for {child.username}
        </h2>
        <p style={modalStyles.text}>
          Choose 3-5 simple words your child can remember and say clearly.
        </p>
        <form onSubmit={handleSetPassphrase}>
          <input
            type="text"
            value={passphrase}
            onChange={(e) => setPassphrase(e.target.value)}
            placeholder="e.g. purple dinosaur jumps high"
            style={modalStyles.input}
            autoFocus
            aria-label="Passphrase"
          />
          {error && <p style={modalStyles.error}>{error}</p>}
          <div style={modalStyles.buttonRow}>
            <button
              type="submit"
              disabled={!passphrase.trim() || submitting}
              style={modalStyles.primaryButton}
            >
              {submitting ? "Saving..." : "Set Passphrase"}
            </button>
            <button type="button" onClick={onClose} style={modalStyles.secondaryButton}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================================================
// 14.5 — Spoken Variant Recording Flow
// ============================================================================

interface SpokenVariantRecordingProps {
  childId: string;
  childName: string;
  onClose: () => void;
  onRecorded: () => void;
}

export function SpokenVariantRecording({
  childId,
  childName,
  onClose,
  onRecorded,
}: SpokenVariantRecordingProps) {
  const [state, setState] = useState<"idle" | "recording" | "submitting" | "done">("idle");
  const [error, setError] = useState("");
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const recorder = newAudioRecorder(stream);
      mediaRecorderRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType || "audio/webm" });
        submitRecording(blob);
      };

      recorder.start();
      setState("recording");
    } catch {
      setError("Could not access microphone.");
    }
  }, []);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
      mediaRecorderRef.current.stop();
      setState("submitting");
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  async function submitRecording(blob: Blob) {
    try {
      const buffer = await blob.arrayBuffer();
      const base64 = bytesToBase64(new Uint8Array(buffer));
      await family.recordSpokenVariant(childId, base64);
      setState("done");
      onRecorded();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save recording");
      setState("idle");
    }
  }

  if (state === "done") {
    return (
      <div style={modalStyles.overlay} role="dialog" aria-modal="true">
        <div style={modalStyles.content}>
          <h2 style={modalStyles.title}>Recorded! ✅</h2>
          <p style={modalStyles.text}>
            {childName}&apos;s spoken variant has been saved.
          </p>
          <button onClick={onClose} style={modalStyles.primaryButton}>
            Done
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={modalStyles.overlay} role="dialog" aria-modal="true">
      <div style={modalStyles.content}>
        <h2 style={modalStyles.title}>Record {childName} Speaking</h2>
        <p style={modalStyles.text}>
          Have {childName} say their secret phrase out loud. This helps match their voice.
        </p>

        {error && <p style={modalStyles.error}>{error}</p>}

        {state === "idle" && (
          <button onClick={startRecording} style={modalStyles.primaryButton}>
            🎤 Start Recording
          </button>
        )}
        {state === "recording" && (
          <button onClick={stopRecording} style={modalStyles.dangerButton}>
            ⏹ Stop Recording
          </button>
        )}
        {state === "submitting" && (
          <p style={modalStyles.text}>Saving...</p>
        )}

        <div style={{ marginTop: 16 }}>
          <button onClick={onClose} style={modalStyles.secondaryButton}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// 14.6 — Co-Parent Invitation Acceptance Page
// ============================================================================

interface AcceptInvitationPageProps {
  token: string;
}

export function AcceptInvitationPage({ token }: AcceptInvitationPageProps) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  async function handleAccept(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim() || !password.trim()) return;
    setSubmitting(true);
    setError("");
    try {
      await family.acceptInvitation(token, email.trim(), password);
      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to accept invitation");
    } finally {
      setSubmitting(false);
    }
  }

  if (success) {
    return (
      <div style={pageStyles.container}>
        <div style={pageStyles.card}>
          <h1 style={pageStyles.title}>Welcome! 🎉</h1>
          <p style={pageStyles.text}>
            You&apos;ve joined the family account. You can now log in with your email and password.
          </p>
          <a href="/" style={modalStyles.primaryButton}>
            Go to Login
          </a>
        </div>
      </div>
    );
  }

  return (
    <div style={pageStyles.container}>
      <div style={pageStyles.card}>
        <h1 style={pageStyles.title}>Join Family Account</h1>
        <p style={pageStyles.text}>
          You&apos;ve been invited to join a family on world by me. Create your account below.
        </p>
        <form onSubmit={handleAccept}>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Your email"
            style={modalStyles.input}
            autoFocus
            aria-label="Email"
          />
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Choose a password"
            style={modalStyles.input}
            aria-label="Password"
          />
          {error && <p style={modalStyles.error}>{error}</p>}
          <button
            type="submit"
            disabled={!email.trim() || !password.trim() || submitting}
            style={modalStyles.primaryButton}
          >
            {submitting ? "Creating Account..." : "Join Family"}
          </button>
        </form>
      </div>
    </div>
  );
}

// ============================================================================
// 14.7 — Email Verification Pending Screen
// ============================================================================

interface EmailVerificationPendingProps {
  email: string;
}

export function EmailVerificationPending({ email }: EmailVerificationPendingProps) {
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  const [error, setError] = useState("");

  async function handleResend() {
    setResending(true);
    setError("");
    try {
      await emailVerification.resend();
      setResent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to resend");
    } finally {
      setResending(false);
    }
  }

  return (
    <div style={pageStyles.container}>
      <div style={pageStyles.card}>
        <h1 style={pageStyles.title}>Check Your Email ✉️</h1>
        <p style={pageStyles.text}>
          We sent a verification link to <strong>{email}</strong>.
          Click the link to activate your account.
        </p>
        {resent ? (
          <p style={{ color: "#4CAF50", marginBottom: 16 }}>
            Verification email resent!
          </p>
        ) : (
          <button
            onClick={handleResend}
            disabled={resending}
            style={modalStyles.secondaryButton}
          >
            {resending ? "Sending..." : "Resend Verification Email"}
          </button>
        )}
        {error && <p style={modalStyles.error}>{error}</p>}
      </div>
    </div>
  );
}

// ============================================================================
// Shared Styles
// ============================================================================

const modalStyles: Record<string, React.CSSProperties> = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(0,0,0,0.5)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
  },
  content: {
    background: "#fff",
    padding: 32,
    borderRadius: 16,
    width: "90%",
    maxWidth: 400,
    textAlign: "center",
  },
  title: {
    fontSize: 20,
    fontWeight: 600,
    marginBottom: 12,
    color: "#333",
  },
  text: {
    fontSize: 15,
    color: "#555",
    marginBottom: 16,
    lineHeight: 1.4,
  },
  input: {
    width: "100%",
    padding: 12,
    borderRadius: 8,
    border: "1px solid #ddd",
    fontSize: 16,
    marginBottom: 12,
    boxSizing: "border-box" as const,
  },
  error: {
    color: "#e53935",
    fontSize: 14,
    marginBottom: 12,
  },
  buttonRow: {
    display: "flex",
    gap: 8,
    justifyContent: "center",
  },
  primaryButton: {
    padding: "10px 24px",
    borderRadius: 8,
    border: "none",
    background: "#4CAF50",
    color: "#fff",
    cursor: "pointer",
    fontSize: 16,
    textDecoration: "none",
  },
  secondaryButton: {
    padding: "10px 24px",
    borderRadius: 8,
    border: "1px solid #ddd",
    background: "#fff",
    cursor: "pointer",
    fontSize: 16,
    color: "#333",
  },
  dangerButton: {
    padding: "10px 24px",
    borderRadius: 8,
    border: "none",
    background: "#e53935",
    color: "#fff",
    cursor: "pointer",
    fontSize: 16,
  },
};

const pageStyles: Record<string, React.CSSProperties> = {
  container: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "100vh",
    background: "#f5f5f5",
    padding: 16,
  },
  card: {
    background: "#fff",
    padding: 32,
    borderRadius: 16,
    width: "90%",
    maxWidth: 420,
    textAlign: "center",
    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
  },
  title: {
    fontSize: 24,
    fontWeight: "bold",
    marginBottom: 12,
    color: "#333",
  },
  text: {
    fontSize: 15,
    color: "#555",
    marginBottom: 16,
    lineHeight: 1.4,
  },
};
